import React, { useEffect, useMemo } from "react";
import {
  ActivityData,
  ActivityDataInput,
  NewActivity
} from "@/entities/activities/types";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import { getEnumTKey } from "@/utils/strings";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { EntityData } from "@/services/api/types";
import { useActivityModal } from "@/components/layouts/Activities/hooks/useActivityModal";
import { isEmpty } from "lodash";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import { getMustSelectCompanyText } from "@/entities/activities/helpers";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityType: EntityType;
  entityData: EntityData | undefined;
  pageName: string;
  isEditMode: boolean;
  editActivityData?: ActivityData | null;
  resetPage?: () => void;
}

const CallActivityModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  entityType,
  entityData,
  pageName,
  isEditMode,
  editActivityData,
  resetPage
}) => {
  const { t } = useTranslation();
  const {
    formRef,
    isFormValid,
    setIsFormValid,
    formSetValue,
    handleSetValue,
    isCompanySet,
    participantNames,
    handleSubmit: baseHandleSubmit,
    getBaseFields,
    processFieldsForEditMode,
    isCreatePersonModalOpen,
    setIsCreatePersonModalOpen,
    onPersonCreation,
    effectiveCompanyId
  } = useActivityModal({
    activityType: NewActivity.CALL,
    isOpen,
    setIsOpen,
    entityType,
    entityData,
    pageName,
    isEditMode,
    editActivityData,
    resetPage
  });
  useEffect(() => {
    if (typeof formSetValue === "function" && !isEditMode) {
      if (!isEmpty(participantNames)) {
        const subject = `${t("activities.activityModal.callWith")} ${participantNames.join(",")}`;
        formSetValue("subject", subject, {
          shouldValidate: true
        });
      }
    }
  }, [participantNames, t, formSetValue, isEditMode]);

  const newActivityKey = getEnumTKey(
    "activities",
    "newActivities",
    NewActivity,
    NewActivity.CALL
  );

  const formSections = useMemo<FormSectionProps[]>(() => {
    const baseFields = getBaseFields();
    const summaryField: FormFieldNewType = {
      name: "summary",
      label: t("activities.activityModal.callSummary"),
      type: "textEditor",
      required: false,
      labelPosition: "top",
      fieldActionProps: {
        placeholder: t("activities.activityModal.callSummaryPlaceholder")
      }
    };

    const callFieldNames = [
      "date",
      "time",
      "participants",
      "subject",
      "summary"
    ];

    const allFields = { ...baseFields, summary: summaryField };
    let fields = callFieldNames
      .map((name) => allFields[name as keyof typeof allFields])
      .filter(Boolean);

    fields = processFieldsForEditMode(fields);

    return [
      {
        id: "call-activity-form",
        fields
      }
    ];
  }, [getBaseFields, processFieldsForEditMode, t]);

  const handleSubmit = async (data: ActivityDataInput) => {
    await baseHandleSubmit(data, (baseData) => ({
      ...baseData,
      ...Object.fromEntries(
        Object.entries(data).filter(
          ([key]) =>
            !["date", "subject", "participants", "summary", "time"].includes(
              key
            )
        )
      )
    }));
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={setIsOpen}
        contentClassname="w-[35rem]"
        
        title={
          <Text as="div" variant="lgSubtitleH1">
            {t(`activities.activityModal.${isEditMode ? "edit" : "add"}`, {
              type: t(newActivityKey).toLowerCase(),
              activity: ""
            })}
          </Text>
        }
        content={
          <div
            className={`flex flex-col gap-2 w-[35rem] p-6 h-fit overflow-y-auto max-h-[75vh]`}
          >
            {/* Hidden element to capture initial focus */}
            <div tabIndex={0} className="sr-only" />
            <FormNew
              sections={formSections}
              isFormValid={(isValid) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
            />
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              data-test="cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Tooltip>
              <TooltipTrigger>
                <Button
                  data-test="submit-button"
                  disabled={!isFormValid || !isCompanySet}
                  onClick={handleCreateNew}
                >
                  {t(isEditMode ? "save" : "activities.activityModal.add", {
                    type: t(newActivityKey).toLowerCase(),
                    activity: ""
                  })}
                </Button>
              </TooltipTrigger>
              {!isCompanySet && (
                <TooltipContent maxWidth={15}>
                  {getMustSelectCompanyText(t, entityType)}
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        }
      />
      <CreatePersonModal
        isOpen={isCreatePersonModalOpen}
        setIsOpen={setIsCreatePersonModalOpen}
        onSuccess={onPersonCreation}
        companyId={effectiveCompanyId}
      />
    </>
  );
};

export default CallActivityModal;
